/**
 * Outreach lists — /crm/outreach (docs/crm-outreach-lists-plan.md §B7).
 *
 * Every outreach list the viewer can see: CRM admins see all of them (a segmented control narrows
 * to "Mine"), recruiters see the lists they are on. One row per list — name, study, status in
 * words, who started it and when, how far through it is (finished of everyone actually on it),
 * and the four numbers a manager reads a list by: replied, interested, moved ahead, no response.
 *
 * Lists are CREATED from a search run, not here (plan §B3): the "New outreach list" button says so
 * and sends an admin to Search. A list that is still being set up is polled quickly so its status
 * flips to Active without a reload; otherwise the page refreshes on the CRM's 60 s rhythm and on
 * stats-changed.
 *
 * An api that does not serve outreach lists (plan §8 push order — 404/5xx) gets a quiet "not
 * available yet" state instead of an error box: the page is reachable by URL even while the
 * sidebar item is hidden.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, ListChecks, Loader2, SearchCheck, Zap } from 'lucide-react';
import { PageHeader, RefreshButton } from '../components';
import { FilterChip, SegmentedControl } from '../components/filters';
import { Alert, Badge, useConfirm } from '../components/ui';
import { RowLink, useRowNav } from '../components/shared';
import EmptyState from '../../ui/EmptyState';
import { STATS_CHANGED_EVENT, useIsCRMAdmin } from '../lib';
import { isUnavailable, listOutreachLists, outreachErrorMessage } from './api';
import type { OutreachListSummary } from './types';
import {
  LIST_STATUS_LABELS,
  LIST_STATUS_TONE,
  launchProgressLine,
  listProgress,
  normalizeListSummary,
} from './outreachSurfaces';
import { useListWorkThrough } from './OutreachTodaySection';

const thCls = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const tdNum = 'px-3 py-2.5 text-right text-sm tabular-nums text-gray-700 dark:text-gray-200';

const shortDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
};

/** Finished-of-total bar with the numbers beside it (the numbers carry the meaning, not the color). */
export function ProgressBar({ finished, total, pct }: { finished: number; total: number; pct: number }) {
  return (
    <div className="flex min-w-[8rem] items-center gap-2">
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={`${finished} of ${total} finished`}
      >
        <div className="h-full rounded-full bg-primary-600 dark:bg-primary-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="whitespace-nowrap text-xs tabular-nums text-gray-500 dark:text-gray-400">
        {finished.toLocaleString()} of {total.toLocaleString()}
      </span>
    </div>
  );
}

export default function OutreachListsPage() {
  const navigate = useNavigate();
  const rowNav = useRowNav();
  const confirm = useConfirm();
  const isAdmin = useIsCRMAdmin();
  const work = useListWorkThrough();

  const [scope, setScope] = useState<'all' | 'mine'>(isAdmin ? 'all' : 'mine');
  const [showClosed, setShowClosed] = useState(false);
  const [lists, setLists] = useState<OutreachListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  // A recruiter only ever has "mine" — the api refuses scope=all to non-admins.
  const effectiveScope = isAdmin ? scope : 'mine';

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const rows = await listOutreachLists(effectiveScope);
      setLists(rows.map(normalizeListSummary));
      setError(null);
      setUnavailable(false);
    } catch (e) {
      // A background refresh that fails keeps what is on screen — a dropped poll must not turn
      // a page full of lists into "not available". Only a load the user asked for says so.
      if (silent) return;
      if (isUnavailable(e)) setUnavailable(true);
      else setError(outreachErrorMessage(e, "Couldn't load the outreach lists. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [effectiveScope]);

  const anyLaunching = lists.some((l) => l.status === 'launching');

  useEffect(() => {
    load();
  }, [load]);

  // Quick while a list is being set up (so "Setting up — 40 of 212" moves), the CRM's usual
  // minute otherwise; stats-changed catches an outcome logged elsewhere in this tab.
  useEffect(() => {
    if (unavailable) return undefined;
    const silent = () => load(true);
    const timer = window.setInterval(silent, anyLaunching ? 5_000 : 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, silent);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, silent);
    };
  }, [load, anyLaunching, unavailable]);

  const visible = useMemo(
    () => lists.filter((l) => showClosed || l.status !== 'completed'),
    [lists, showClosed],
  );
  const closedCount = lists.length - lists.filter((l) => l.status !== 'completed').length;

  const explainNew = async () => {
    const go = await confirm({
      title: 'Outreach lists start from a search',
      message:
        'Open a search (or build one), run it, and press "Start an outreach list" on the results. ' +
        "You'll pick the study, the recruiters to split the people between, and the first message.",
      confirmLabel: 'Go to Search',
      cancelLabel: 'Not now',
    });
    if (go) navigate('/crm/search');
  };

  if (unavailable) {
    return (
      <div className="space-y-4">
        <PageHeader icon={ListChecks} title="Outreach lists" />
        <EmptyState
          variant="card"
          icon={ListChecks}
          title="Outreach lists aren't available yet"
          hint="This part of the CRM is still being switched on, or couldn't be reached. Check back soon."
          action={
            <button
              type="button"
              onClick={() => { setUnavailable(false); load(); }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={ListChecks}
        title="Outreach lists"
        count={loading ? null : visible.length}
        subtitle={
          isAdmin
            ? 'People split between recruiters to work through for one study, by call and text.'
            : 'The lists a manager gave you to work through.'
        }
        actions={
          <>
            {isAdmin && (
              <SegmentedControl
                label="Whose lists"
                value={scope}
                onChange={(v) => setScope(v === 'mine' ? 'mine' : 'all')}
                options={[
                  { value: 'all', label: 'All lists' },
                  { value: 'mine', label: 'Mine' },
                ]}
              />
            )}
            {closedCount > 0 && (
              <FilterChip active={showClosed} onClick={() => setShowClosed((v) => !v)} icon={<Archive className="h-4 w-4" />}>
                Closed ({closedCount})
              </FilterChip>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={explainNew}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
              >
                <SearchCheck className="h-4 w-4" aria-hidden="true" />
                New outreach list
              </button>
            )}
            <RefreshButton onClick={() => load()} loading={loading} label="Refresh outreach lists" />
          </>
        }
      />

      {error && (
        <Alert
          tone="error"
          icon={false}
          actions={<button type="button" onClick={() => load()} className="font-medium underline hover:no-underline">Retry</button>}
        >
          {error}
        </Alert>
      )}

      {work.emptyLine && <Alert tone="info" size="sm">{work.emptyLine}</Alert>}
      {work.errorLine && <Alert tone="error" size="sm">{work.errorLine}</Alert>}

      {loading && lists.length === 0 ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : visible.length === 0 ? (
        lists.length > 0 ? (
          <EmptyState
            variant="card"
            icon={ListChecks}
            title="No open outreach lists"
            hint="Every list here has been closed."
            action={
              <button type="button" onClick={() => setShowClosed(true)} className="text-sm font-medium text-primary-700 hover:underline dark:text-primary-400">
                Show closed lists
              </button>
            }
          />
        ) : isAdmin && effectiveScope === 'all' ? (
          <EmptyState
            variant="card"
            icon={ListChecks}
            title="No outreach lists yet"
            hint={
              <>
                An outreach list starts from a search. Open or build a search in <strong>Search</strong>, run it,
                then press <strong>Start an outreach list</strong> — you'll pick the study, the recruiters to split
                it between, and the first message.
              </>
            }
            action={
              <button
                type="button"
                onClick={() => navigate('/crm/search')}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
              >
                <SearchCheck className="h-4 w-4" aria-hidden="true" />
                Go to Search
              </button>
            }
          />
        ) : (
          <EmptyState
            variant="card"
            icon={ListChecks}
            title="You're not on any outreach lists"
            hint="When a manager gives you an outreach list, it shows up here and on Today."
          />
        )
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className={thCls}>List</th>
                <th className={thCls}>Status</th>
                <th className={thCls}>Started</th>
                <th className={thCls}>Progress</th>
                <th className={`${thCls} text-right`} title="Open people with a reply waiting on us">Replied</th>
                <th className={`${thCls} text-right`} title="Said they were interested">Interested</th>
                <th className={`${thCls} text-right`} title="Went on to be scheduled, screened or enrolled">Moved ahead</th>
                <th className={`${thCls} text-right`} title="No response after every try">No response</th>
                <th className={thCls}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {visible.map((l) => {
                const url = `/crm/outreach/${l.id}`;
                const prog = listProgress(l.counts);
                // Any open person of mine: the start itself says "next tries start …" when
                // nobody is due, and due callbacks aren't in the replied/untouched/retry counts.
                const canWork = !work.sessionActive && l.status === 'active' && (l.mine?.open ?? 0) > 0;
                return (
                  <tr
                    key={l.id}
                    {...rowNav(url)}
                    className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="max-w-[18rem] px-3 py-2.5">
                      <RowLink to={url} title={l.name} className="block truncate text-sm font-medium text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400">
                        {l.name}
                      </RowLink>
                      <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{l.study.title}</span>
                      {l.mine && l.mine.open > 0 && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          Yours: {l.mine.open.toLocaleString()} still open
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={LIST_STATUS_TONE[l.status]} size="sm" dot>{LIST_STATUS_LABELS[l.status]}</Badge>
                      {l.launch_error && (
                        <span className="mt-0.5 block max-w-[14rem] text-xs text-amber-700 dark:text-amber-400">{l.launch_error}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300">
                      {l.created_by?.name || 'Someone'}
                      <span className="block text-xs text-gray-400 dark:text-gray-500">{shortDate(l.launched_at || l.created_at)}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {l.status === 'launching'
                        ? <span className="text-xs text-gray-500 dark:text-gray-400">{launchProgressLine(null)}</span>
                        : <ProgressBar {...prog} />}
                    </td>
                    <td className={`${tdNum} ${l.counts.replied > 0 ? 'font-semibold text-gray-900 dark:text-gray-100' : ''}`}>
                      {l.counts.replied.toLocaleString()}
                    </td>
                    <td className={tdNum}>{l.counts.reached.toLocaleString()}</td>
                    <td className={tdNum}>{l.counts.advanced.toLocaleString()}</td>
                    <td className={tdNum}>{l.counts.no_response.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right">
                      {canWork && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); work.start(l.id); }}
                          disabled={work.starting}
                          aria-label={`Work through ${l.name}`}
                          className="inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-primary-600 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
                        >
                          {work.busyFor === l.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                            : <Zap className="h-3.5 w-3.5" aria-hidden="true" />}
                          Work through
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
